import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  ControlButton,
  type Node,
  type Edge,
  type NodeTypes,
  type NodeProps,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ArrowLeft,
  Layers,
  X,
  Server as ServerIcon,
  Maximize2,
  Minimize2,
  Lock,
  Unlock,
  Filter,
  Check,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════ */
interface InvItem {
  id: string;
  hostname: string;
  ip: string;
  status: "Active" | "Standby" | "DR";
  /** Full BE device payload for the drilldown panel (optional). */
  device?: DeviceDetails;
}

export interface DeviceDetails {
  id?: string;
  orgId?: string;
  name?: string;
  type?: string;
  status?: string;
  ipAddress?: string;
  hostname?: string;
  osName?: string;
  osVersion?: string;
  cpuCores?: number;
  ramGb?: number;
  diskGb?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

interface CtrlItem {
  id: string;
  controlId: string;
  title: string;
}

interface CompNode {
  id: string;
  name: string;
  type: string;
  status: "operational" | "under-development";
  tag?: string;
  inventory: InvItem[];
  controls: CtrlItem[];
}

interface SspNode {
  id: string;
  title: string;
  subtitle: string;
  color: string;
  sspCount: string;
  components: CompNode[];
}

export interface AppData {
  appName: string;
  appSubtitle: string;
  totalInventory: number;
  totalComponents: number;
  totalSatisfied: number;
  totalNotSatisfied: number;
  ssps: SspNode[];
  /** When true, the App root node is suppressed and the SSPs become the
   *  graph's roots — used in Service mode where there is no Application. */
  hideAppRoot?: boolean;
}

export type NodeTypeKey =
  | "appNode"
  | "sspNode"
  | "compNode"
  | "invNode"
  | "ctrlNode";

/* ═══════════════════════════════════════════════════════════
   MOCK DATA — Realistic services (LDAP, DNS, PKI, etc.)
   ═══════════════════════════════════════════════════════════ */
export function buildMockData(
  appName: string,
  selectedSspIds: string[],
): AppData {
  const ALL_SSPS: SspNode[] = [
    {
      id: "ldap-ssp",
      title: "LDAP Directory SSP",
      subtitle: "System Security Plan → Directory Svc",
      color: "emerald",
      sspCount: "5 : 1",
      components: [
        {
          id: "comp-openldap",
          name: "OpenLDAP Server",
          type: "Directory Server",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-ldap1",
              hostname: "ldap-prod-dc01",
              ip: "10.0.5.10",
              status: "Active",
            },
            {
              id: "inv-ldap2",
              hostname: "ldap-prod-dc02",
              ip: "10.0.5.11",
              status: "Active",
            },
            {
              id: "inv-ldap3",
              hostname: "ldap-dr-dc01",
              ip: "10.1.5.10",
              status: "DR",
            },
          ],
          controls: [
            {
              id: "ctrl-ia2-ldap",
              controlId: "IA-2",
              title: "Identification & Authentication",
            },
            {
              id: "ctrl-ac2-ldap",
              controlId: "AC-2",
              title: "Account Management",
            },
            {
              id: "ctrl-ia5-ldap",
              controlId: "IA-5",
              title: "Authenticator Management",
            },
          ],
        },
        {
          id: "comp-ad-dc",
          name: "AD Domain Controller",
          type: "Active Directory",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-ad1",
              hostname: "ad-dc-prod-01",
              ip: "10.0.5.20",
              status: "Active",
            },
            {
              id: "inv-ad2",
              hostname: "ad-dc-prod-02",
              ip: "10.0.5.21",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-ac3-ad",
              controlId: "AC-3",
              title: "Access Enforcement",
            },
            { id: "ctrl-ac6-ad", controlId: "AC-6", title: "Least Privilege" },
            {
              id: "ctrl-ac7-ad",
              controlId: "AC-7",
              title: "Unsuccessful Logon Attempts",
            },
          ],
        },
        {
          id: "comp-ldap-proxy",
          name: "LDAP Proxy",
          type: "Connection Proxy",
          status: "operational",
          inventory: [
            {
              id: "inv-lp1",
              hostname: "ldap-proxy-01",
              ip: "10.0.5.15",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-sc7-lp",
              controlId: "SC-7",
              title: "Boundary Protection",
            },
            {
              id: "ctrl-sc8-lp",
              controlId: "SC-8",
              title: "Transmission Confidentiality",
            },
          ],
        },
      ],
    },
    {
      id: "dns-ssp",
      title: "DNS Service SSP",
      subtitle: "System Security Plan → Name Resolution",
      color: "sky",
      sspCount: "4 : 0",
      components: [
        {
          id: "comp-bind-primary",
          name: "BIND DNS Primary",
          type: "Authoritative DNS",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-dns1",
              hostname: "dns-auth-prod-01",
              ip: "10.0.0.53",
              status: "Active",
            },
            {
              id: "inv-dns2",
              hostname: "dns-auth-prod-02",
              ip: "10.0.0.54",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-sc20-dns",
              controlId: "SC-20",
              title: "Secure Name Resolution",
            },
            {
              id: "ctrl-sc22-dns",
              controlId: "SC-22",
              title: "Architecture & Provisioning",
            },
          ],
        },
        {
          id: "comp-dns-resolver",
          name: "DNS Resolver Cluster",
          type: "Recursive Resolver",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-dnsres1",
              hostname: "dns-resolver-east-01",
              ip: "10.0.0.55",
              status: "Active",
            },
            {
              id: "inv-dnsres2",
              hostname: "dns-resolver-west-01",
              ip: "10.1.0.55",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-sc21-res",
              controlId: "SC-21",
              title: "Secure Name Resolution (Recursive)",
            },
            {
              id: "ctrl-si4-res",
              controlId: "SI-4",
              title: "System Monitoring",
            },
          ],
        },
        {
          id: "comp-dnssec",
          name: "DNSSEC Signer",
          type: "Zone Signing",
          status: "operational",
          inventory: [
            {
              id: "inv-dnssec1",
              hostname: "dnssec-signer-01",
              ip: "10.0.0.56",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-sc12-sec",
              controlId: "SC-12",
              title: "Crypto Key Establishment",
            },
          ],
        },
      ],
    },
    {
      id: "pki-ssp",
      title: "PKI / Certificate Authority SSP",
      subtitle: "System Security Plan → Cert Mgmt",
      color: "violet",
      sspCount: "4 : 1",
      components: [
        {
          id: "comp-root-ca",
          name: "Root CA (Offline)",
          type: "Root Certificate Authority",
          status: "operational",
          inventory: [
            {
              id: "inv-rootca",
              hostname: "root-ca-hsm-01",
              ip: "Air-Gapped",
              status: "Standby",
            },
          ],
          controls: [
            {
              id: "ctrl-sc12-rca",
              controlId: "SC-12",
              title: "Crypto Key Management",
            },
            {
              id: "ctrl-sc13-rca",
              controlId: "SC-13",
              title: "Cryptographic Protection",
            },
          ],
        },
        {
          id: "comp-issuing-ca",
          name: "Issuing CA",
          type: "Subordinate CA",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-ica1",
              hostname: "issuing-ca-prod-01",
              ip: "10.0.5.30",
              status: "Active",
            },
            {
              id: "inv-ica2",
              hostname: "issuing-ca-prod-02",
              ip: "10.0.5.31",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-ia7-ica",
              controlId: "IA-7",
              title: "Crypto Module Authentication",
            },
            {
              id: "ctrl-sc17-ica",
              controlId: "SC-17",
              title: "PKI Certificates",
            },
            {
              id: "ctrl-sc8-ica",
              controlId: "SC-8",
              title: "Transmission Confidentiality",
            },
          ],
        },
        {
          id: "comp-ocsp",
          name: "OCSP Responder",
          type: "Certificate Validation",
          status: "operational",
          inventory: [
            {
              id: "inv-ocsp1",
              hostname: "ocsp-responder-01",
              ip: "10.0.5.35",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-ia5-ocsp",
              controlId: "IA-5",
              title: "Authenticator Management",
            },
          ],
        },
        {
          id: "comp-certmgr",
          name: "cert-manager",
          type: "Certificate Lifecycle",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-cm1",
              hostname: "cert-manager-k8s",
              ip: "10.0.1.60",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-cm3-cm",
              controlId: "CM-3",
              title: "Configuration Change Control",
            },
            {
              id: "ctrl-sa10-cm",
              controlId: "SA-10",
              title: "Developer Configuration Mgmt",
            },
          ],
        },
      ],
    },
    {
      id: "siem-ssp",
      title: "SIEM & Log Management SSP",
      subtitle: "System Security Plan → Security Monitoring",
      color: "rose",
      sspCount: "6 : 2",
      components: [
        {
          id: "comp-splunk-idx",
          name: "Splunk Indexer",
          type: "Log Indexing",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-idx1",
              hostname: "splunk-idx-cls-01",
              ip: "10.0.10.100",
              status: "Active",
            },
            {
              id: "inv-idx2",
              hostname: "splunk-idx-cls-02",
              ip: "10.0.10.101",
              status: "Active",
            },
            {
              id: "inv-idx3",
              hostname: "splunk-idx-cls-03",
              ip: "10.0.10.102",
              status: "Active",
            },
          ],
          controls: [
            { id: "ctrl-au2-idx", controlId: "AU-2", title: "Event Logging" },
            {
              id: "ctrl-au12-idx",
              controlId: "AU-12",
              title: "Audit Record Generation",
            },
            {
              id: "ctrl-au6-idx",
              controlId: "AU-6",
              title: "Audit Review & Reporting",
            },
          ],
        },
        {
          id: "comp-splunk-sh",
          name: "Splunk Search Head",
          type: "Log Search & Analysis",
          status: "operational",
          inventory: [
            {
              id: "inv-sh1",
              hostname: "splunk-search-01",
              ip: "10.0.10.110",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-ir4-sh",
              controlId: "IR-4",
              title: "Incident Handling",
            },
            {
              id: "ctrl-ir5-sh",
              controlId: "IR-5",
              title: "Incident Monitoring",
            },
          ],
        },
        {
          id: "comp-fwd-fleet",
          name: "Forwarder Fleet",
          type: "Log Collection Agent",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-fwd1",
              hostname: "uf-fleet-manager",
              ip: "10.0.10.120",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-au3-fwd",
              controlId: "AU-3",
              title: "Content of Audit Records",
            },
            {
              id: "ctrl-au9-fwd",
              controlId: "AU-9",
              title: "Protection of Audit Info",
            },
          ],
        },
        {
          id: "comp-soar",
          name: "SOAR Playbooks",
          type: "Automated Response",
          status: "operational",
          inventory: [
            {
              id: "inv-soar1",
              hostname: "soar-engine-01",
              ip: "10.0.10.130",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-ir6-soar",
              controlId: "IR-6",
              title: "Incident Reporting",
            },
            {
              id: "ctrl-si2-soar",
              controlId: "SI-2",
              title: "Flaw Remediation",
            },
          ],
        },
      ],
    },
    {
      id: "ntp-ssp",
      title: "NTP Time Sync SSP",
      subtitle: "System Security Plan → Time Sync",
      color: "amber",
      sspCount: "2 : 0",
      components: [
        {
          id: "comp-ntp-pool",
          name: "NTP Stratum-2 Pool",
          type: "Time Server",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-ntp1",
              hostname: "ntp-stratum2-east",
              ip: "10.0.5.123",
              status: "Active",
            },
            {
              id: "inv-ntp2",
              hostname: "ntp-stratum2-west",
              ip: "10.1.5.123",
              status: "Active",
            },
          ],
          controls: [
            { id: "ctrl-au8-ntp", controlId: "AU-8", title: "Time Stamps" },
            {
              id: "ctrl-au8-1-ntp",
              controlId: "AU-8(1)",
              title: "Synchronization with Auth Source",
            },
          ],
        },
        {
          id: "comp-gps-ref",
          name: "GPS Reference Clock",
          type: "Stratum-1 Source",
          status: "operational",
          inventory: [
            {
              id: "inv-gps1",
              hostname: "gps-receiver-dc1",
              ip: "10.0.5.124",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-pe14-gps",
              controlId: "PE-14",
              title: "Environmental Controls",
            },
          ],
        },
      ],
    },
    {
      id: "vault-ssp",
      title: "Secrets Management SSP",
      subtitle: "System Security Plan → Vault Svc",
      color: "slate",
      sspCount: "4 : 0",
      components: [
        {
          id: "comp-vault-cls",
          name: "Vault Cluster",
          type: "Secrets Engine",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-vault1",
              hostname: "vault-prod-01",
              ip: "10.0.6.10",
              status: "Active",
            },
            {
              id: "inv-vault2",
              hostname: "vault-prod-02",
              ip: "10.0.6.11",
              status: "Active",
            },
            {
              id: "inv-vault3",
              hostname: "vault-prod-03",
              ip: "10.0.6.12",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-sc28-vault",
              controlId: "SC-28",
              title: "Protection of Info at Rest",
            },
            {
              id: "ctrl-sc12-vault",
              controlId: "SC-12",
              title: "Crypto Key Establishment",
            },
            {
              id: "ctrl-ac3-vault",
              controlId: "AC-3",
              title: "Access Enforcement",
            },
          ],
        },
        {
          id: "comp-transit",
          name: "Transit Engine",
          type: "Encryption-as-a-Service",
          status: "operational",
          inventory: [
            {
              id: "inv-transit1",
              hostname: "vault-transit-01",
              ip: "10.0.6.10",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-sc13-te",
              controlId: "SC-13",
              title: "Cryptographic Protection",
            },
            {
              id: "ctrl-sc8-te",
              controlId: "SC-8",
              title: "Transmission Confidentiality",
            },
          ],
        },
        {
          id: "comp-approle",
          name: "AppRole Auth",
          type: "Machine Authentication",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-approle1",
              hostname: "vault-auth-backend",
              ip: "10.0.6.10",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-ia2-approle",
              controlId: "IA-2",
              title: "Identification & Authentication",
            },
            {
              id: "ctrl-ia8-approle",
              controlId: "IA-8",
              title: "Non-Org User Identification",
            },
          ],
        },
      ],
    },
    {
      id: "smtp-ssp",
      title: "SMTP / Email Relay SSP",
      subtitle: "System Security Plan → Email Svc",
      color: "sky",
      sspCount: "3 : 0",
      components: [
        {
          id: "comp-postfix",
          name: "Postfix Relay",
          type: "Mail Transfer Agent",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-smtp1",
              hostname: "smtp-relay-prod-01",
              ip: "10.0.7.25",
              status: "Active",
            },
            {
              id: "inv-smtp2",
              hostname: "smtp-relay-prod-02",
              ip: "10.0.7.26",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-sc8-smtp",
              controlId: "SC-8",
              title: "Transmission Confidentiality",
            },
            {
              id: "ctrl-si8-smtp",
              controlId: "SI-8",
              title: "Spam Protection",
            },
          ],
        },
        {
          id: "comp-dkim",
          name: "DKIM Signer",
          type: "Email Authentication",
          status: "operational",
          inventory: [
            {
              id: "inv-dkim1",
              hostname: "dkim-signer-01",
              ip: "10.0.7.27",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-sc23-dkim",
              controlId: "SC-23",
              title: "Session Authenticity",
            },
            {
              id: "ctrl-au3-dkim",
              controlId: "AU-3",
              title: "Content of Audit Records",
            },
          ],
        },
      ],
    },
    {
      id: "backup-ssp",
      title: "Backup & Recovery SSP",
      subtitle: "System Security Plan → Data Protection",
      color: "amber",
      sspCount: "4 : 0",
      components: [
        {
          id: "comp-veeam",
          name: "Veeam Backup Server",
          type: "Backup Orchestrator",
          status: "operational",
          tag: "Shared Svc",
          inventory: [
            {
              id: "inv-veeam1",
              hostname: "veeam-backup-01",
              ip: "10.0.8.10",
              status: "Active",
            },
          ],
          controls: [
            { id: "ctrl-cp9-veeam", controlId: "CP-9", title: "System Backup" },
            {
              id: "ctrl-cp10-veeam",
              controlId: "CP-10",
              title: "System Recovery",
            },
            {
              id: "ctrl-cp6-veeam",
              controlId: "CP-6",
              title: "Alternate Storage Site",
            },
          ],
        },
        {
          id: "comp-s3-backup",
          name: "S3 Backup Target",
          type: "Object Storage",
          status: "operational",
          inventory: [
            {
              id: "inv-s3bk1",
              hostname: "s3-backup-vault",
              ip: "N/A (S3)",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-sc28-s3b",
              controlId: "SC-28",
              title: "Protection of Info at Rest",
            },
            { id: "ctrl-mp2-s3b", controlId: "MP-2", title: "Media Access" },
          ],
        },
        {
          id: "comp-dr-repl",
          name: "DR Replication",
          type: "Cross-Region Replication",
          status: "operational",
          inventory: [
            {
              id: "inv-dr1",
              hostname: "dr-replication-agent",
              ip: "10.1.8.10",
              status: "Active",
            },
          ],
          controls: [
            {
              id: "ctrl-cp7-dr",
              controlId: "CP-7",
              title: "Alternate Processing Site",
            },
            { id: "ctrl-cp8-dr", controlId: "CP-8", title: "Telecom Services" },
          ],
        },
      ],
    },
  ];

  const selectedSSPs = ALL_SSPS.filter((s) => selectedSspIds.includes(s.id));

  let totalInv = 0;
  let totalComp = 0;
  let totalSat = 0;
  let totalNotSat = 0;

  for (const ssp of selectedSSPs) {
    for (const comp of ssp.components) {
      totalComp++;
      totalInv += comp.inventory.length;
      totalSat += comp.controls.length;
      if (comp.status === "under-development") totalNotSat++;
    }
  }

  return {
    appName,
    appSubtitle: "Application SSP",
    totalInventory: totalInv,
    totalComponents: totalComp,
    totalSatisfied: totalSat,
    totalNotSatisfied: totalNotSat,
    ssps: selectedSSPs,
  };
}

/* ═══════════════════════════════════════════════════════════
   COLOR MAPS — matching overview dashboard palette
   ═══════════════════════════════════════════════════════════ */
const TC: Record<string, { c: string; bg: string; bd: string }> = {
  sky: { c: "#38bdf8", bg: "#38bdf818", bd: "#38bdf840" },
  amber: { c: "#fbbf24", bg: "#fbbf2418", bd: "#fbbf2440" },
  violet: { c: "#a78bfa", bg: "#a78bfa18", bd: "#a78bfa40" },
  emerald: { c: "#34d399", bg: "#34d39918", bd: "#34d39940" },
  rose: { c: "#fb7185", bg: "#fb718518", bd: "#fb718540" },
  slate: { c: "#94a3b8", bg: "#94a3b818", bd: "#94a3b840" },
};
const COL = {
  // Brighter surface than #1a2332 so cards pop on the dotted canvas.
  sf: "#243044",
  sf2: "#2a3650",
  bd: "#475569",
  bd2: "#334155",
  tx: "#f1f5f9",
  tx2: "#94a3b8",
  tx3: "#64748b",
  application: "#818cf8",
  applicationBg: "#818cf818",
  ssp: "#a78bfa",
  sspBg: "#a78bfa18",
  component: "#22d3ee",
  componentBg: "#22d3ee18",
  inventory: "#fb923c",
  inventoryBg: "#fb923c18",
  control: "#a78bfa",
  controlBg: "#a78bfa18",
  passed: "#34d399",
  passedBg: "#34d39918",
  failed: "#f87171",
  failedBg: "#f8717118",
};

// SVG icon paths (matching overview dashboard)
const IC: Record<string, string> = {
  application: "M4 4h16v16H4V4zm2 2v12h12V6H12v5l-2-1.5L8 11V6H6z",
  ssp: "M9 12l-2 2-4-4 1.4-1.4L7 11.2l3.6-3.6L12 9l-3 3zm0-6l-2 2-4-4L4.4 2.6 7 5.2l3.6-3.6L12 3 9 6zm0 12l-2 2-4-4 1.4-1.4L7 17.2l3.6-3.6L12 15l-3 3zM13 7h8v2h-8V7zm0 6h8v2h-8v-2zm0 6h8v2h-8v-2z",
  component:
    "M20 7l-8-4-8 4v10l8 4 8-4V7zM12 5.3l5.3 2.6L12 10.6 6.7 7.9 12 5.3zM5 8.8l6 3v6.4l-6-3V8.8zm8 9.4v-6.4l6-3v6.4l-6 3z",
  inventory: "M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z",
  control: "M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z",
};

/* ═══════════════════════════════════════════════════════════
   CUSTOM NODE COMPONENTS — overview dashboard style
   260×64 cards, 3px left border, inline styles
   ═══════════════════════════════════════════════════════════ */
const NW = 260;
const NH = 64;

function NodeIcon({
  path,
  color,
  bg,
}: {
  path: string;
  color: string;
  bg: string;
}) {
  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: 6,
        background: bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        border: `1px solid ${color}20`,
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24">
        <path d={path} fill={color} />
      </svg>
    </div>
  );
}

function StatusDot({ color }: { color: string }) {
  return (
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        boxShadow: `0 0 0 2px ${color}20`,
        flexShrink: 0,
      }}
    />
  );
}

function FilterPopover({
  open,
  onOpenChange,
  visibleTypes,
  onToggle,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  visibleTypes: Record<NodeTypeKey, boolean>;
  onToggle: (key: NodeTypeKey) => void;
}) {
  const items: Array<{ key: NodeTypeKey; label: string; color: string }> = [
    { key: "appNode", label: "Application", color: COL.application },
    { key: "sspNode", label: "Service", color: COL.ssp },
    { key: "compNode", label: "Component", color: COL.component },
    { key: "invNode", label: "Inventory", color: COL.inventory },
    { key: "ctrlNode", label: "Control", color: COL.control },
  ];
  const activeCount = items.filter((i) => visibleTypes[i.key]).length;

  // Anchor the dropdown to the button's bounding rect using `position: fixed`
  // so an `overflow-hidden` ancestor (the canvas wrapper) can't clip it.
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const update = () => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (!rect) return;
      const popoverWidth = 192; // w-48
      // Right-align the popover under the button.
      setPos({ top: rect.bottom + 6, left: rect.right - popoverWidth });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-xs font-medium d hover:text-foreground hover:bg-muted/40 transition-colors"
      >
        <Filter className="size-3.5" />
        Filter
        <span className="ml-0.5 text-[10px] font-bold text-foreground/80 px-1 py-px rounded bg-muted/60">
          {activeCount}/{items.length}
        </span>
      </button>
      {open &&
        pos &&
        createPortal(
          <>
            {/* click-outside catcher */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => onOpenChange(false)}
            />
            <div
              className="fixed z-[9999] w-48 rounded-lg border border-border bg-card shadow-lg p-1 animate-in fade-in zoom-in-95 duration-100"
              style={{ top: pos.top, left: pos.left }}
            >
              {items.map((it) => {
                const checked = visibleTypes[it.key];
                return (
                  <button
                    key={it.key}
                    type="button"
                    onClick={() => onToggle(it.key)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-foreground hover:bg-muted/50 transition-colors"
                  >
                    <span
                      className={`size-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                        checked
                          ? "bg-indigo-600 border-indigo-600"
                          : "border-border bg-card"
                      }`}
                    >
                      {checked && (
                        <Check
                          className="size-2.5 text-white"
                          strokeWidth={3.5}
                        />
                      )}
                    </span>
                    <span
                      className="size-2 rounded-sm flex-shrink-0"
                      style={{
                        background: it.color,
                        boxShadow: `0 0 0 1px ${it.color}33`,
                      }}
                    />
                    <span className="flex-1 text-left">{it.label}</span>
                  </button>
                );
              })}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

function LegendDot({
  color,
  label,
  onClick,
}: {
  color: string;
  label: string;
  onClick?: () => void;
}) {
  const dot = (
    <span
      className="inline-block size-1.5 rounded-sm"
      style={{
        background: color,
        boxShadow: `0 0 0 1px ${color}33`,
      }}
    />
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={`Zoom to ${label}`}
        className="flex items-center gap-1 rounded px-1 py-0.5 d hover:text-foreground hover:bg-foreground/5 active:scale-[0.97] transition-all"
      >
        {dot}
        {label}
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1.5 px-1.5 py-0.5 d">
      {dot}
      {label}
    </span>
  );
}

// ── Application Root Node ──
function AppNode({ data }: NodeProps) {
  const tc = COL.application;
  return (
    <div
      style={{
        width: NW,
        height: NH,
        background: COL.sf,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 8px",
        borderRadius: 9,
        border: `1px solid ${COL.bd}`,
        borderLeft: `3px solid ${tc}`,
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        cursor: "pointer",
      }}
    >
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: tc, width: 5, height: 5, border: "none" }}
      />
      <NodeIcon path={IC.application} color={tc} bg={COL.applicationBg} />
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: COL.tx,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {data.label as string}
        </div>
        <div
          style={{
            fontSize: 9,
            color: COL.tx2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          {data.subtitle as string}
          <span
            style={{
              fontSize: 7,
              padding: "0 4px",
              borderRadius: 3,
              fontWeight: 600,
              background: COL.applicationBg,
              color: tc,
            }}
          >
            {data.stats as string}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── SSP Node ──
function SspFlowNode({ data }: NodeProps) {
  const colors = TC[data.color as string] || TC.slate;
  const tc = colors.c;
  return (
    <div
      style={{
        width: NW,
        height: NH,
        background: COL.sf,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 8px",
        borderRadius: 9,
        border: `1px solid ${COL.bd}`,
        borderLeft: `3px solid ${tc}`,
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        cursor: "pointer",
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: COL.tx3, width: 5, height: 5, border: "none" }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: COL.tx3, width: 5, height: 5, border: "none" }}
      />
      <NodeIcon path={IC.ssp} color={tc} bg={colors.bg} />
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: COL.tx,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {data.label as string}
        </div>
        <div
          style={{
            fontSize: 9,
            color: COL.tx2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          {data.subtitle as string}
          <span
            style={{
              fontSize: 7,
              padding: "0 4px",
              borderRadius: 3,
              fontWeight: 600,
              background: colors.bg,
              color: tc,
            }}
          >
            {data.sspCount as string}
          </span>
        </div>
      </div>
      <StatusDot color={COL.passed} />
    </div>
  );
}

// ── Component Node ──
function CompFlowNode({ data }: NodeProps) {
  const tc = COL.component;
  return (
    <div
      style={{
        width: NW,
        height: NH,
        background: COL.sf,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 8px",
        borderRadius: 9,
        border: `1px solid ${COL.bd}`,
        borderLeft: `3px solid ${tc}`,
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        cursor: "pointer",
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: COL.tx3, width: 5, height: 5, border: "none" }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: COL.tx3, width: 5, height: 5, border: "none" }}
      />
      <NodeIcon path={IC.component} color={tc} bg={COL.componentBg} />
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: COL.tx,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {data.label as string}
        </div>
        <div
          style={{
            fontSize: 9,
            color: COL.tx2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          {data.subtitle as string}
          {Boolean(data.tag) && (
            <span
              style={{
                fontSize: 7,
                padding: "0 4px",
                borderRadius: 3,
                fontWeight: 600,
                background: COL.componentBg,
                color: tc,
              }}
            >
              {data.tag as string}
            </span>
          )}
        </div>
      </div>
      <div
        style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}
      >
        <span style={{ fontSize: 10, fontWeight: 600, color: COL.passed }}>
          {data.ctrlCount as string}
        </span>
        <StatusDot color={COL.passed} />
      </div>
    </div>
  );
}

// ── Inventory Node ──
function InvFlowNode({ data }: NodeProps) {
  const tc = COL.inventory;
  const stColor =
    data.status === "Active"
      ? COL.passed
      : data.status === "Standby"
        ? "#fbbf24"
        : "#60a5fa";
  return (
    <div
      style={{
        width: NW,
        height: NH,
        background: COL.sf,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 8px",
        borderRadius: 9,
        border: `1px solid ${COL.bd}`,
        borderLeft: `3px solid ${tc}`,
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        cursor: "pointer",
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: COL.tx3, width: 5, height: 5, border: "none" }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: COL.tx3, width: 5, height: 5, border: "none" }}
      />
      <NodeIcon path={IC.inventory} color={tc} bg={COL.inventoryBg} />
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: COL.tx,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {data.label as string}
        </div>
        <div
          style={{
            fontSize: 9,
            color: COL.tx2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          {data.ip as string}
          <span
            style={{
              fontSize: 8,
              padding: "0 4px",
              borderRadius: 3,
              fontWeight: 600,
              background: `${stColor}18`,
              color: stColor,
            }}
          >
            {data.status as string}
          </span>
        </div>
      </div>
      <StatusDot color={stColor} />
    </div>
  );
}

// ── Control Node ──
function CtrlFlowNode({ data }: NodeProps) {
  const tc = COL.control;
  return (
    <div
      style={{
        width: NW,
        height: NH,
        background: COL.sf,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 8px",
        borderRadius: 9,
        border: `1px solid ${COL.bd}`,
        borderLeft: `3px solid ${tc}`,
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        cursor: "pointer",
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: COL.tx3, width: 5, height: 5, border: "none" }}
      />
      <NodeIcon path={IC.control} color={tc} bg={COL.controlBg} />
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: COL.tx,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {(data.controlId as string)?.toUpperCase()}
        </div>
        <div
          style={{
            fontSize: 9,
            color: COL.tx2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {data.title as string}
        </div>
      </div>
      <StatusDot color={COL.passed} />
    </div>
  );
}

export const inheritanceNodeTypes: NodeTypes = {
  appNode: AppNode,
  sspNode: SspFlowNode,
  compNode: CompFlowNode,
  invNode: InvFlowNode,
  ctrlNode: CtrlFlowNode,
};

/* ═══════════════════════════════════════════════════════════
   GRAPH BUILDER
   ═══════════════════════════════════════════════════════════ */
export function buildFlowGraph(data: AppData, showDetails: boolean) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Positioning constants (horizontal layout: left → right)
  // Layout constants — matching overview dashboard.
  // In Service mode the App column is hidden, so every downstream column
  // shifts left by one slot to keep the graph centered.
  const HG = 52; // horizontal gap between columns
  const VG = 5; // vertical gap between sibling nodes
  const colShift = data.hideAppRoot ? -(NW + HG) : 0;
  const COL_APP = 0 + colShift;
  const COL_SSP = NW + HG + colShift;
  const COL_COMP = COL_SSP + NW + HG;
  const COL_INV = COL_COMP + NW + HG;
  const COL_CTRL = COL_INV + NW + HG;
  const SSP_SPACING = 30; // extra gap between SSP groups

  // ── Bottom-up height calculation ──
  // Each leaf = NH. Each parent = sum of children heights + gaps.
  // This prevents overlapping wires.

  // Step 1: Calculate subtree height for each component
  interface CompLayout {
    comp: (typeof data.ssps)[0]["components"][0];
    height: number;
  }
  interface SspLayout {
    ssp: (typeof data.ssps)[0];
    compLayouts: CompLayout[];
    height: number;
  }

  const sspLayouts: SspLayout[] = [];

  for (const ssp of data.ssps) {
    const compLayouts: CompLayout[] = [];
    for (const comp of ssp.components) {
      if (showDetails) {
        // Each inventory item gets a row; controls without inventory also get a row
        const rowCount = Math.max(comp.inventory.length, comp.controls.length);
        const h = rowCount * (NH + VG) - VG;
        compLayouts.push({ comp, height: Math.max(h, NH) });
      } else {
        compLayouts.push({ comp, height: NH });
      }
    }
    // Total SSP height = sum of all component subtree heights + gaps between them
    const totalH =
      compLayouts.reduce((s, cl) => s + cl.height, 0) +
      (compLayouts.length - 1) * VG;
    sspLayouts.push({ ssp, compLayouts, height: Math.max(totalH, NH) });
  }

  // Total canvas height
  const totalHeight =
    sspLayouts.reduce((s, sl) => s + sl.height, 0) +
    (sspLayouts.length - 1) * SSP_SPACING;

  // ── Place nodes top-down using calculated heights ──

  // App root — centered vertically. Skipped in Service mode where the
  // service itself is the natural root.
  if (!data.hideAppRoot) {
    const appY = totalHeight / 2 - NH / 2;
    nodes.push({
      id: "app-root",
      type: "appNode",
      position: { x: COL_APP, y: appY },
      data: {
        label: data.appName,
        subtitle: data.appSubtitle,
        stats: `${data.totalComponents} comps · ${data.totalInventory} inv`,
      },
    });
  }

  let cursorY = 0; // running Y position

  for (const sl of sspLayouts) {
    const ssp = sl.ssp;
    const sspNodeId = `ssp-${ssp.id}`;

    // SSP node — centered within its subtree height
    const sspCenterY = cursorY + sl.height / 2 - NH / 2;
    nodes.push({
      id: sspNodeId,
      type: "sspNode",
      position: { x: COL_SSP, y: sspCenterY },
      data: {
        label: ssp.title,
        subtitle: ssp.subtitle,
        color: ssp.color,
        sspCount: ssp.sspCount,
      },
    });

    if (!data.hideAppRoot) {
      edges.push({
        id: `app-${sspNodeId}`,
        source: "app-root",
        target: sspNodeId,
        type: "default",
        style: { stroke: "#94a3b8", strokeWidth: 1.5 },
      });
    }

    // Edge color per SSP
    const edgeColorMap: Record<string, string> = {
      sky: "#38bdf8",
      amber: "#fbbf24",
      violet: "#a78bfa",
      emerald: "#34d399",
      rose: "#fb7185",
      slate: "#94a3b8",
    };
    const sspEdgeColor = edgeColorMap[ssp.color] || "#94a3b8";

    // Place components within SSP block
    let compCursorY = cursorY;

    for (const cl of sl.compLayouts) {
      const comp = cl.comp;
      const compNodeId = `comp-${comp.id}`;

      // Component — centered within its subtree height
      const compCenterY = compCursorY + cl.height / 2 - NH / 2;
      nodes.push({
        id: compNodeId,
        type: "compNode",
        position: { x: COL_COMP, y: compCenterY },
        data: {
          label: comp.name,
          subtitle: comp.type,
          tag: comp.tag || "",
          color: ssp.color,
          ctrlCount: `${comp.controls.length}`,
          invCount: `${comp.inventory.length}`,
        },
      });

      edges.push({
        id: `${sspNodeId}-${compNodeId}`,
        source: sspNodeId,
        target: compNodeId,
        type: "default",
        style: { stroke: sspEdgeColor, strokeWidth: 1.5 },
      });

      // Detail nodes — stacked sequentially (no centering, no overlap)
      if (showDetails) {
        const rowCount = Math.max(comp.inventory.length, comp.controls.length);

        for (let ri = 0; ri < rowCount; ri++) {
          const rowY = compCursorY + ri * (NH + VG);

          // Inventory node for this row
          if (ri < comp.inventory.length) {
            const inv = comp.inventory[ri];
            const invNodeId = `inv-${inv.id}`;

            nodes.push({
              id: invNodeId,
              type: "invNode",
              position: { x: COL_INV, y: rowY },
              data: {
                label: inv.hostname,
                ip: inv.ip,
                status: inv.status,
                device: inv.device,
              },
            });

            edges.push({
              id: `${compNodeId}-${invNodeId}`,
              source: compNodeId,
              target: invNodeId,
              type: "default",
              style: { stroke: "#94a3b8", strokeWidth: 1 },
            });

            // Control connected from this inventory
            if (ri < comp.controls.length) {
              const ctrl = comp.controls[ri];
              const ctrlNodeId = `ctrl-${ctrl.id}`;

              nodes.push({
                id: ctrlNodeId,
                type: "ctrlNode",
                position: { x: COL_CTRL, y: rowY },
                data: { controlId: ctrl.controlId, title: ctrl.title },
              });

              edges.push({
                id: `${invNodeId}-${ctrlNodeId}`,
                source: invNodeId,
                target: ctrlNodeId,
                type: "default",
                style: { stroke: "#94a3b8", strokeWidth: 1 },
              });
            }
          } else if (ri < comp.controls.length) {
            // Control without matching inventory — connect from component
            const ctrl = comp.controls[ri];
            const ctrlNodeId = `ctrl-${ctrl.id}`;

            nodes.push({
              id: ctrlNodeId,
              type: "ctrlNode",
              position: { x: COL_CTRL, y: rowY },
              data: { controlId: ctrl.controlId, title: ctrl.title },
            });

            edges.push({
              id: `${compNodeId}-${ctrlNodeId}`,
              source: compNodeId,
              target: ctrlNodeId,
              type: "default",
              style: { stroke: "#94a3b8", strokeWidth: 1 },
            });
          }
        }
      }

      compCursorY += cl.height + VG;
    }

    cursorY += sl.height + SSP_SPACING;
  }

  return { nodes, edges };
}

/* ═══════════════════════════════════════════════════════════
   INNER FLOW COMPONENT
   ═══════════════════════════════════════════════════════════ */
// Side panel reserves this much horizontal space; the graph re-fits into the
// remaining width when it opens.
const PANEL_WIDTH = 320; // matches w-80 (Tailwind 80 = 20rem = 320px)

function FlowInner({
  data,
  showDetails,
  visibleTypes,
  onPanelOpenChange,
}: {
  data: AppData;
  showDetails: boolean;
  visibleTypes: Record<NodeTypeKey, boolean>;
  onPanelOpenChange?: (open: boolean) => void;
}) {
  const { nodes: builtNodes, edges: builtEdges } = useMemo(
    () => buildFlowGraph(data, showDetails),
    [data, showDetails],
  );

  // Apply the type-visibility filter — drop hidden nodes and any edges
  // that touch them. fitView re-runs (deps include nodes.length) so the
  // remaining content recenters automatically.
  const { nodes: rawNodes, edges: rawEdges } = useMemo(() => {
    const visibleIds = new Set(
      builtNodes
        .filter(
          (n) => visibleTypes[(n.type as NodeTypeKey) ?? "appNode"] !== false,
        )
        .map((n) => n.id),
    );
    const ns = builtNodes.filter((n) => visibleIds.has(n.id));
    const es = builtEdges.filter(
      (e) => visibleIds.has(e.source) && visibleIds.has(e.target),
    );
    return { nodes: ns, edges: es };
  }, [builtNodes, builtEdges, visibleTypes]);

  // Hover state — when set, we highlight the hovered node and its full
  // ancestor/descendant chain, dimming everything else.
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Build adjacency maps once per edge set so the hover lookup stays O(N).
  const { parentsOf, childrenOf } = useMemo(() => {
    const parents = new Map<string, string[]>();
    const children = new Map<string, string[]>();
    for (const e of rawEdges) {
      if (!children.has(e.source)) children.set(e.source, []);
      children.get(e.source)!.push(e.target);
      if (!parents.has(e.target)) parents.set(e.target, []);
      parents.get(e.target)!.push(e.source);
    }
    return { parentsOf: parents, childrenOf: children };
  }, [rawEdges]);

  // BFS up + down from the hovered id to collect every connected node.
  const connectedIds = useMemo(() => {
    if (!hoveredId) return null;
    const set = new Set<string>([hoveredId]);
    const upQueue = [hoveredId];
    while (upQueue.length) {
      const id = upQueue.shift()!;
      for (const p of parentsOf.get(id) ?? []) {
        if (!set.has(p)) {
          set.add(p);
          upQueue.push(p);
        }
      }
    }
    const downQueue = [hoveredId];
    while (downQueue.length) {
      const id = downQueue.shift()!;
      for (const c of childrenOf.get(id) ?? []) {
        if (!set.has(c)) {
          set.add(c);
          downQueue.push(c);
        }
      }
    }
    return set;
  }, [hoveredId, parentsOf, childrenOf]);

  // Apply visual dimming + blur to non-connected nodes/edges. Lit nodes
  // pop forward; everything else fades and softens so the focused chain
  // reads instantly even on a dense graph.
  const nodes = useMemo(() => {
    if (!connectedIds) return rawNodes;
    return rawNodes.map((n) => {
      const lit = connectedIds.has(n.id);
      return {
        ...n,
        style: {
          ...(n.style ?? {}),
          opacity: lit ? 1 : 0.12,
          filter: lit ? "none" : "blur(1.5px) saturate(0.6)",
          transition: "opacity 180ms ease-out, filter 180ms ease-out",
        },
      };
    });
  }, [rawNodes, connectedIds]);

  const edges = useMemo(() => {
    if (!connectedIds) return rawEdges;
    return rawEdges.map((e) => {
      const lit = connectedIds.has(e.source) && connectedIds.has(e.target);
      return {
        ...e,
        animated: lit,
        style: {
          ...(e.style ?? {}),
          opacity: lit ? 1 : 0.06,
          strokeWidth: lit ? 2 : 1,
          transition: "opacity 180ms ease-out, stroke-width 180ms ease-out",
        },
      };
    });
  }, [rawEdges, connectedIds]);

  const flow = useReactFlow();
  const fullscreenRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = fullscreenRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen?.();
    }
  }, []);

  // Drilldown panel — currently scoped to inventory (device) nodes; clicking
  // any other node closes it. Easy to extend to other node types by
  // branching on `node.type` in the click handler.
  const [activeDevice, setActiveDevice] = useState<{
    label: string;
    device: DeviceDetails;
  } | null>(null);

  // Zoom + center the canvas on every node of a given type. Used by the
  // legend chips so users can jump straight to "all components" or "all
  // inventory" without manual panning.
  const focusNodeType = useCallback(
    (nodeType: string) => {
      const targets = nodes.filter((n) => n.type === nodeType);
      if (targets.length === 0) return;
      flow.fitView({
        nodes: targets.map((n) => ({ id: n.id })),
        padding: 0.4,
        duration: 400,
        maxZoom: 1.4,
      });
    },
    [nodes, flow],
  );

  const handleNodeClick = useCallback((_e: React.MouseEvent, node: Node) => {
    if (node.type === "invNode" && node.data?.device) {
      setActiveDevice({
        label: (node.data.label as string) ?? "Device",
        device: node.data.device as DeviceDetails,
      });
    } else {
      setActiveDevice(null);
    }
  }, []);

  // Smart initial zoom — small graphs (1-2 nodes) feel lost at fitView's
  // default. Scale the minimum allowed zoom so the chart fills the canvas
  // even when there's very little content. ReactFlow's fitView will pick
  // the largest zoom that fits, capped by maxZoom.
  const totalNodes = nodes.length;
  const dynamicMaxZoom =
    totalNodes <= 3 ? 1.6 : totalNodes <= 8 ? 1.3 : totalNodes <= 20 ? 1.1 : 1;

  // Re-fit the graph when the panel opens/closes OR when the canvas size
  // changes (e.g. entering/exiting fullscreen). Without the small delay,
  // fitView runs before the browser has reflowed the new dimensions and
  // the content stays anchored top-left instead of recentering.
  useEffect(() => {
    const id = window.setTimeout(() => {
      flow.fitView({ padding: 0.2, duration: 300, maxZoom: dynamicMaxZoom });
    }, 80);
    return () => window.clearTimeout(id);
  }, [activeDevice, totalNodes, dynamicMaxZoom, flow, isFullscreen]);

  // Notify the embedding host when the side panel toggles so it can hide
  // overlapping chrome (filter chip, etc.) while the panel is open.
  useEffect(() => {
    onPanelOpenChange?.(!!activeDevice);
  }, [activeDevice, onPanelOpenChange]);

  return (
    // Flex row layout — left flex-child holds the graph, right flex-child
    // holds the panel. The panel's width animates from 0 → PANEL_WIDTH so
    // the graph shrinks alongside it, and ReactFlow's fitView keeps the
    // whole graph visible inside the new width. Nothing overlaps.
    <div
      ref={fullscreenRef}
      className="relative w-full h-full flex overflow-hidden bg-background"
    >
      <div className="flex-1 min-w-0 h-full">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={inheritanceNodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: dynamicMaxZoom }}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={!isLocked}
          panOnDrag={!isLocked}
          zoomOnScroll={!isLocked}
          zoomOnPinch={!isLocked}
          zoomOnDoubleClick={!isLocked}
          nodesConnectable={false}
          onNodeClick={handleNodeClick}
          onNodeMouseEnter={(_e, n) => setHoveredId(n.id)}
          onNodeMouseLeave={() => setHoveredId(null)}
          onPaneClick={() => setActiveDevice(null)}
        >
          {/* Dotted background — softer dot, slightly tighter gap so
              the canvas reads as a workspace, not a flat surface. */}
          <Background
            variant={BackgroundVariant.Dots}
            gap={18}
            size={1.4}
            color="#475569"
          />
          <Controls
            position="bottom-right"
            showInteractive={false}
            className="!bg-card !border-border !shadow-sm [&>button]:!bg-card [&>button]:!border-border [&>button]:!d [&>button:hover]:!bg-accent"
          >
            <ControlButton
              onClick={() => setIsLocked((v) => !v)}
              title={isLocked ? "Unlock canvas" : "Lock canvas"}
            >
              {isLocked ? (
                <Lock className="size-3" />
              ) : (
                <Unlock className="size-3" />
              )}
            </ControlButton>
            <ControlButton
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="size-3" />
              ) : (
                <Maximize2 className="size-3" />
              )}
            </ControlButton>
          </Controls>

          {/* Legend — bottom-left. Each chip is a quick-jump (clicking it
              zooms the canvas onto every node of that type). Only types
              actually present in the current graph are rendered, so an
              app with no controls won't show a "Control" chip.
              Hidden while the side panel is open to avoid overlap. */}
          {!activeDevice &&
            (() => {
              const presentTypes = new Set(rawNodes.map((n) => n.type));
              const items: Array<{
                type: string;
                color: string;
                label: string;
              }> = [
                {
                  type: "appNode",
                  color: COL.application,
                  label: "Application",
                },
                { type: "sspNode", color: COL.ssp, label: "Service" },
                { type: "compNode", color: COL.component, label: "Component" },
                { type: "invNode", color: COL.inventory, label: "Inventory" },
                { type: "ctrlNode", color: COL.control, label: "Control" },
              ].filter((i) => presentTypes.has(i.type));
              if (items.length === 0) return null;
              return (
                <div className="absolute bottom-2 left-2 z-10 rounded-md bg-card/25 hover:bg-card/60 transition-colors duration-200 px-1.5 py-1">
                  <div className="flex items-center gap-0.5 text-[9px] font-semibold">
                    {items.map((i) => (
                      <LegendDot
                        key={i.type}
                        color={i.color}
                        label={i.label}
                        onClick={() => focusNodeType(i.type)}
                      />
                    ))}
                  </div>
                </div>
              );
            })()}
        </ReactFlow>
      </div>

      {/* Side panel — width snaps to the final value so layout cost is
          paid once; the panel itself slides in via translateX (GPU-only,
          no layout thrash) so the motion stays at 60fps even while
          ReactFlow tweens its viewport in the adjacent flex child. */}
      <div
        className="h-full flex-shrink-0 overflow-hidden"
        style={{ width: activeDevice ? PANEL_WIDTH : 0 }}
      >
        <div
          className="h-full transition-transform duration-300 ease-out"
          style={{
            width: PANEL_WIDTH,
            transform: activeDevice ? "translateX(0)" : "translateX(100%)",
          }}
        >
          {activeDevice && (
            <DeviceDetailsPanel
              label={activeDevice.label}
              device={activeDevice.device}
              onClose={() => setActiveDevice(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DEVICE DRILLDOWN PANEL
   Right-anchored side panel — matches ComponentDetailsPanel
   pattern from the visualizer (fixed inset-y-0, w-80).
   ═══════════════════════════════════════════════════════════ */
function DeviceDetailsPanel({
  label,
  device,
  onClose,
}: {
  label: string;
  device: DeviceDetails;
  onClose: () => void;
}) {
  // Display labels map — friendly names for known fields. Unknown fields
  // fall back to camelCase split (e.g. "ipAddress" → "Ip Address").
  const FIELD_LABELS: Record<string, string> = {
    id: "ID",
    orgId: "Org ID",
    name: "Name",
    type: "Type",
    status: "Status",
    ipAddress: "IP Address",
    hostname: "Hostname",
    osName: "OS",
    osVersion: "OS Version",
    cpuCores: "CPU Cores",
    ramGb: "RAM (GB)",
    diskGb: "Disk (GB)",
    createdAt: "Created",
    updatedAt: "Updated",
  };

  // Order matters — render most-relevant fields first.
  const ORDER = [
    "name",
    "hostname",
    "ipAddress",
    "type",
    "status",
    "osName",
    "osVersion",
    "cpuCores",
    "ramGb",
    "diskGb",
  ];

  // Internal/audit fields the user doesn't need in the panel.
  const HIDDEN_KEYS = new Set(["id", "orgId", "createdAt", "updatedAt"]);

  const fmt = (key: string, value: unknown): string => {
    if (value === null || value === undefined) return "—";
    if (key === "createdAt" || key === "updatedAt") {
      try {
        return new Date(value as string).toLocaleString();
      } catch {
        return String(value);
      }
    }
    if (typeof value === "number") return String(value);
    return String(value);
  };

  const labelOf = (key: string): string =>
    FIELD_LABELS[key] ??
    key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

  // Pull rows in the canonical order, then append any extra unknown fields
  // (so future BE additions show up automatically). Audit-only keys
  // (HIDDEN_KEYS) are skipped from both passes.
  const seen = new Set<string>();
  const rows: Array<{ key: string; value: unknown }> = [];
  for (const k of ORDER) {
    if (HIDDEN_KEYS.has(k)) continue;
    if (k in device) {
      rows.push({ key: k, value: device[k] });
      seen.add(k);
    }
  }
  for (const k of Object.keys(device)) {
    if (seen.has(k) || HIDDEN_KEYS.has(k)) continue;
    rows.push({ key: k, value: device[k] });
  }

  const stColor =
    device.status === "ACTIVE"
      ? COL.passed
      : device.status === "STANDBY"
        ? "#fbbf24"
        : "#60a5fa";

  return (
    <aside
      className="h-full w-full bg-card border-l border-border shadow-xl flex flex-col"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      {/* Header */}
      <div className="p-4 border-b border-border flex-shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="size-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: COL.inventoryBg }}
            >
              <ServerIcon className="size-4" style={{ color: COL.inventory }} />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-foreground leading-tight truncate">
                {label}
              </h3>
              <span
                className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wide"
                style={{ background: `${stColor}20`, color: stColor }}
              >
                {device.status || "—"}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-muted d hover:text-foreground transition-colors flex-shrink-0"
            aria-label="Close device details"
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="device-panel-scroll flex-1 overflow-y-auto p-4">
        <div className="space-y-1">
          {rows.map(({ key, value }) => (
            <div
              key={key}
              className="flex items-start gap-3 py-1.5 border-b border-border/50 last:border-0"
            >
              <span className="text-[10px] font-bold d tracking-wide w-24 flex-shrink-0 pt-0.5">
                {labelOf(key)}
              </span>
              <span className="text-xs text-foreground break-all flex-1">
                {fmt(key, value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

/* ═══════════════════════════════════════════════════════════
   EXPORTED COMPONENT
   ═══════════════════════════════════════════════════════════ */
interface InheritanceFlowViewProps {
  appName: string;
  selectedSspIds: string[];
  onBack: () => void;
  /** When provided, skip the mock builder and render this real graph data. */
  data?: AppData;
  /** When true, the inner header (Back / title / inheritance label) is
   *  suppressed because the embedding modal already provides those. The
   *  Filter control is repositioned to a floating top-right chip so it
   *  stays accessible on the canvas. */
  embedded?: boolean;
}

export function InheritanceFlowView({
  appName,
  selectedSspIds,
  onBack,
  data: dataProp,
  embedded = false,
}: InheritanceFlowViewProps) {
  const data = useMemo(
    () => dataProp ?? buildMockData(appName, selectedSspIds),
    [dataProp, appName, selectedSspIds],
  );

  // Filter state — controls which node types render on the canvas. All on
  // by default; toggling a checkbox hides every node (and its edges) of
  // that type.
  const [visibleTypes, setVisibleTypes] = useState<
    Record<NodeTypeKey, boolean>
  >({
    appNode: true,
    sspNode: true,
    compNode: true,
    invNode: true,
    ctrlNode: true,
  });
  const [filterOpen, setFilterOpen] = useState(false);
  // True while a node detail side-panel is open inside FlowInner — used to
  // hide overlapping chrome (the embedded Filter chip).
  const [panelOpen, setPanelOpen] = useState(false);

  const toggleType = useCallback(
    (key: NodeTypeKey) => setVisibleTypes((v) => ({ ...v, [key]: !v[key] })),
    [],
  );

  return (
    <div
      className="flex flex-col h-full min-h-0 relative"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      {/* ── Header Bar — hidden when embedded; the host modal owns the
            chrome (back arrow, app name, close). ──────────────────── */}
      {!embedded && (
        <div className="flex-shrink-0 px-5 py-3 border-b border-border bg-card/80 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium d hover:text-foreground hover:bg-muted/40 transition-colors"
              >
                <ArrowLeft className="size-3.5" />
                Back to SSPs
              </button>
              <div className="h-5 w-px bg-border" />
              <div className="flex items-center gap-2">
                <div className="size-7 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                  <Layers className="size-3.5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">
                    {data.appName}
                  </p>
                  <p className="text-[10px] d">
                    Inheritance Flow Visualization
                  </p>
                </div>
              </div>
            </div>

            <FilterPopover
              open={filterOpen}
              onOpenChange={setFilterOpen}
              visibleTypes={visibleTypes}
              onToggle={toggleType}
            />
          </div>
        </div>
      )}

      {/* ── Flow Canvas ────────────────────────────── */}
      <div className="flex-1 min-h-0 relative">
        <ReactFlowProvider>
          <FlowInner
            data={data}
            showDetails={true}
            visibleTypes={visibleTypes}
            onPanelOpenChange={setPanelOpen}
          />
        </ReactFlowProvider>

        {/* Embedded mode → floating Filter chip in the canvas's top-right
            corner so the user can still toggle node types without the
            full header. Hidden while the side detail panel is open so it
            doesn't overlap the panel header. */}
        {embedded && !panelOpen && (
          <div className="absolute top-3 right-3 z-10">
            <FilterPopover
              open={filterOpen}
              onOpenChange={setFilterOpen}
              visibleTypes={visibleTypes}
              onToggle={toggleType}
            />
          </div>
        )}
      </div>
    </div>
  );
}
